import { formatCents, lineTotalCents, parseMoneyToCents, tt } from '@agos/shared'

import type { BillRow } from './outstanding'
import { closeDiscountPayload, manualCents, totalDiscountCents, type PayDiscount } from './pay-discount'
import { lineKey, nameOf, type PayableLine } from './split-payment'

/**
 * A payment on its way, as the dialog draws it - `FRONTEND.md` §9.769, owner O130, FB-1086.
 *
 * **The waiter at Šurany**: *"Zaklikla som šošovicou gazdovsku a veľkú kofolu a ukázalo mi platiť 0e a zmizlo to a
 * zostala tak rajčinová a brocolo. Ale blocik vyšiel správne."* The split moves the ticked lines to a new order before
 * the receipt prints, and the dialog drew from the order the board re-reads every eight seconds, so a read during the
 * print showed the table's remainder and a selection that matched none of its rows - *0,00 €*.
 *
 * **Taken at the press and never read again from the prop.** What the operator confirmed is what the dialog keeps
 * showing until it closes: the lines, the bill's rows, the figure charged. A poll cannot reach any of it, which is the
 * whole guarantee - §9.408 keyed a tick by its row so a moving list could not move it, and here the row itself leaves
 * the order, so the only honest copy is the one taken before it left.
 */

/** One line being paid: what it is called, how many, and what they come to. */
export interface FrozenLine {
  /** `lineKey` of the row it was taken from - stable, and published as a marker. */
  key: number
  name: string
  quantity: number
  cents: number
}

export interface PaymentInFlight {
  /** *Zaplatiť všetko* or *Zaplatiť vybrané*. */
  kind: 'all' | 'split'
  /** The lines being paid. On a split, the ticked quantities and nothing else. */
  lines: FrozenLine[]
  /** What is charged - *Na zaplatenie* at the press, the figure the receipt is printed for. */
  payCents: number
  /** The bill's rows as §9.453 drew them at the press. */
  rows: BillRow[]
  /**
   * The order's lines the receipt is printed for, which the close after *Zaplatiť všetko* pays - the whole bill. Empty
   * on a split: its receipt is printed from the server's own portion.
   */
  printed: PayableLine[]
}

/**
 * The lines being paid, from the lines on screen and how many of each - `wholeBill` or the split's ticks.
 *
 * A quantity is clamped to the line exactly as `splitSelection` clamps it, so the snapshot cannot show more of a line
 * than the payload sends.
 */
export function frozenLines(
  lines: readonly PayableLine[],
  quantities: ReadonlyMap<number, number>,
): FrozenLine[] {
  const frozen: FrozenLine[] = []
  lines.forEach((line, index) => {
    const key = lineKey(line, index)
    const total = line.quantity ?? 0
    const quantity = Math.max(0, Math.min(total, quantities.get(key) ?? 0))
    if (quantity <= 0) return
    frozen.push({
      key,
      name: line.productName ?? line.customName ?? '',
      quantity,
      cents: lineTotalCents(line.unitPrice, quantity),
    })
  })
  return frozen
}

/** A line's identity for the close: what it is and what it costs - `SplitPaymentService.quantitiesByLine`. */
function quantitiesByLine(lines: readonly PayableLine[]): Map<string, number> {
  const byLine = new Map<string, number>()
  for (const line of lines) {
    const quantity = line.quantity ?? 0
    if (quantity <= 0) continue
    const key = `${String(line.productId ?? 0)}|${nameOf(line)}|${String(parseMoneyToCents(line.unitPrice))}`
    byLine.set(key, (byLine.get(key) ?? 0) + quantity)
  }
  return byLine
}

/**
 * Whether the order still holds exactly the lines the receipt was printed for - §9.769.
 *
 * **The server's question, asked first by the screen.** `split-payment` refuses a close whose lines do not add up to
 * the order's (`refuseUnlessConserved`), compared by product, name and unit price and never by position or row id; this
 * is the same comparison, so the dialog can say *why* in its own words when it can already see the change, and the
 * server still refuses when the dialog's copy of the order is behind.
 */
export function sameBill(printed: readonly PayableLine[], live: readonly PayableLine[]): boolean {
  const before = quantitiesByLine(printed)
  const after = quantitiesByLine(live)
  if (before.size !== after.size) return false
  for (const [key, quantity] of before) {
    if (after.get(key) !== quantity) return false
  }
  return true
}

/** The line under the bill while the payment is on its way. */
export function inFlightTitle(): string {
  return tt('Tlačí sa doklad…')
}

export function inFlightNote(kind: PaymentInFlight['kind']): string {
  return kind === 'split'
    ? tt('Platia sa vybrané položky. Okno sa zatvorí, keď pokladňa odpovie.')
    : tt('Platí sa celý účet. Po vytlačení sa opýta, či zatvoriť stôl.')
}

/** Why the close after *Zaplatiť všetko* did not happen, when the order moved under the question - §9.769. */
export function orderChangedNote(payCents: number): string {
  return `${tt('Objednávka sa od vytlačenia dokladu zmenila, preto sa stôl nezatvoril.')} ${tt(
    'Doklad pokrýva',
  )} ${formatCents(payCents)}.`
}

/** The paid portion `split-payment` answers, as much of it as the receipt and the toast need. */
export interface PaidPortion {
  id?: number
  orderNumber?: string
  type?: string
  items?: PayableLine[]
  /** `null` when the answer carries no readable total, so a caller falls back rather than printing nought. */
  totalCents: number | null
  discountCents: number
}

function moneyOf(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? parseMoneyToCents(value) : null
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value.replace(',', '.')))) {
    return parseMoneyToCents(value)
  }
  return null
}

/**
 * The paid portion out of `split-payment`'s answer, or `null` - §9.769, §9.770.
 *
 * **Its `discount` is the zľava the server wrote on the portion** - the share of the order's own plus the one typed -
 * and its `total` is what the portion costs, which is what the receipt registers and what the toast names (FB-1088,
 * FB-1089).
 */
export function paidPortionOf(response: unknown): PaidPortion | null {
  if (typeof response !== 'object' || response === null) return null
  const paid = (response as { paidOrder?: unknown }).paidOrder
  if (typeof paid !== 'object' || paid === null) return null
  const row = paid as Record<string, unknown>
  return {
    ...(typeof row.id === 'number' ? { id: row.id } : {}),
    ...(typeof row.orderNumber === 'string' ? { orderNumber: row.orderNumber } : {}),
    ...(typeof row.type === 'string' ? { type: row.type } : {}),
    ...(Array.isArray(row.items) ? { items: row.items as PayableLine[] } : {}),
    totalCents: moneyOf(row.total),
    discountCents: Math.max(0, moneyOf(row.discount) ?? 0),
  }
}

/**
 * The zľava the order holds once the typed one is written on it - §9.770: the order's own, plus the further one.
 *
 * `existingCents` is the order's own whole zľava - its `discount` column, which is `existingDiscountCents` over the
 * whole bill - and the further one is measured against what is still owed, exactly as `closeDiscountPayload` and the
 * server measure it (§10A.216).
 */
export function recordedDiscountCents(
  existingCents: number,
  discount: PayDiscount,
  dueCents: number,
): number {
  return Math.max(0, existingCents) + totalDiscountCents(discount, dueCents)
}

/**
 * What `PATCH /orders/{id}` is sent to write a zľava typed in *Zaplatiť* on the order before its receipt prints -
 * §9.770, FB-1087. `null` when nothing was typed: an ordinary payment sends no extra request.
 *
 * - **`discount`** is the order's own zľava plus the typed one, in euros as the endpoint reads it; the server
 *   re-derives `total` from it (`recomputeTotals`), so no total is sent - and §10A.397 refuses a bare one.
 * - **The provenance is the close's** (§10A.172, `SplitPaymentService.provenanceOf`): `closeDiscountPayload`'s type
 *   and figure, and who gave it - the chosen waiter, else whoever is signed in - so the owner's discount report reads
 *   the same whether the table was closed at once or left open.
 * - **Only for a manual zľava.** A coupon alone sends `discount` alone, so the server asks *either* right, as the close
 *   does, and a coupon is not relabelled a manual discount.
 */
export function recordedDiscountPayload(
  discount: PayDiscount,
  dueCents: number,
  existingCents: number,
  operator: string | null,
): Record<string, unknown> | null {
  if (totalDiscountCents(discount, dueCents) <= 0) return null
  const body: Record<string, unknown> = {
    discount: recordedDiscountCents(existingCents, discount, dueCents) / 100,
  }
  if ((manualCents(discount, dueCents) ?? 0) <= 0) return body
  const close = closeDiscountPayload(discount, dueCents)
  if (!('discountType' in close)) return body
  return {
    ...body,
    manualDiscountType: close.discountType,
    manualDiscountValue: close.discountAmount,
    manualDiscountOperator: operator,
  }
}

/** What the question after a failed print adds when the zľava was written before it - §9.770, point 5. */
export function discountKeptNote(recordedCents: number): string {
  return `${tt('Zľava')} ${formatCents(recordedCents)} ${tt('zostala zapísaná na objednávke.')}`
}

/**
 * What is said over §9.369's *Zatvoriť stôl* when a zľava is typed on an order whose receipt is out - §9.770, point 4.
 * The close is held: the zľava can reach neither the printed receipt nor, since the close carries none, the order.
 */
export function discountAfterPrintNote(): string {
  return tt('Doklad je vytlačený - ďalšiu zľavu teraz pridať nemožno. Najprv doklad stornujte.')
}
